import {useCallback, useEffect, useMemo, useState} from "react";
import getData from "../../../api/axiosInstance";
import {
    Button,
    Chip,
    Dialog,
    DialogBody, DialogFooter,
    DialogHeader, ListItem,
    Typography
} from "@material-tailwind/react";
import {ArrowLongRightIcon, ArrowLongUpIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {Link} from "react-router-dom";

export default function InventoryValueCard({ analyticsPeriod, onAnalyticsPeriodChange, providedAvailableItems, providedSoldItems }) {
    const [dataValue, setDataValue] = useState(0);
    const [fetchedAvailableItems, setFetchedAvailableItems] = useState([]);
    const [fetchedSoldItems, setFetchedSoldItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [inventoryValueGraph, setInventoryValueGraph] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    const token = localStorage.getItem("token");

    const headers = useMemo(() => {
        return {
            'Authorization': `Bearer ${token}`
        };
    }, [token]);

    useEffect(() => {
        if (analyticsPeriod) return;

        getData(
            "/purchased_items/inventory_value",
            headers,
            setDataValue,
            "Error fetching total inventory value"
        );
    }, [headers, analyticsPeriod]);

    useEffect(() => {
        if (providedAvailableItems) return;

        getData(
            '/purchased_items/available_items',
            headers,
            setFetchedAvailableItems,
            'Error fetching available items'
        );
    }, [headers, providedAvailableItems]);

    useEffect(() => {
        if (providedSoldItems) return;

        getData(
            '/purchased_items/sold_items',
            headers,
            setFetchedSoldItems,
            'Error fetching sold items'
        );
    }, [headers, providedSoldItems]);

    const availableItems = providedAvailableItems || fetchedAvailableItems;
    const soldItems = providedSoldItems || fetchedSoldItems;

    const valueFormatter = (value) =>
        value.toLocaleString('sr-RS', {
            style: 'currency',
            currency: 'RSD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

    const getInventoryValue = useCallback((manga) => {
        return Number.isFinite(manga.expectedSellingPriceInRsd)
            ? manga.expectedSellingPriceInRsd
            : 0;
    }, []);

    const getCombinedItems = useCallback(() => {
        return [
            ...availableItems.map(manga => ({
                ...manga,
                saleDate: null,
            })),
            ...soldItems,
        ];
    }, [availableItems, soldItems]);

    const getMonthRange = useCallback((month, year) => {
        return {
            start: new Date(year, month - 1, 1),
            end: new Date(year, month, 0),
        };
    }, []);

    const getEffectiveEndDate = useCallback((endDate) => {
        const today = new Date();
        return endDate > today ? today : endDate;
    }, []);

    const getFirstPurchaseDate = useCallback(() => {
        const purchaseDates = getCombinedItems()
            .map((item) => new Date(item.purchasedDate))
            .filter((date) => !Number.isNaN(date.getTime()));

        return purchaseDates.length > 0
            ? new Date(Math.min(...purchaseDates))
            : new Date();
    }, [getCombinedItems]);

    const getAnalyticsPeriodRange = useCallback(() => {
        const today = new Date();

        if (!analyticsPeriod || analyticsPeriod.mode === 'month') {
            const month = analyticsPeriod?.month || selectedMonth;
            const year = analyticsPeriod?.year || selectedYear;
            const { start, end } = getMonthRange(month, year);

            return {
                start,
                end,
                effectiveEnd: getEffectiveEndDate(end),
                mode: 'month',
                label: `${start.toLocaleString('en-US', { month: 'long' }).toUpperCase()} ${year}`,
                granularity: 'day',
            };
        }

        if (analyticsPeriod.mode === 'quarter') {
            const startMonth = (analyticsPeriod.quarter - 1) * 3;
            const start = new Date(analyticsPeriod.year, startMonth, 1);
            const end = new Date(analyticsPeriod.year, startMonth + 3, 0);

            return {
                start,
                end,
                effectiveEnd: getEffectiveEndDate(end),
                mode: 'quarter',
                label: `Q${analyticsPeriod.quarter} ${analyticsPeriod.year}`,
                granularity: 'month',
            };
        }

        if (analyticsPeriod.mode === 'year') {
            const start = new Date(analyticsPeriod.year, 0, 1);
            const end = new Date(analyticsPeriod.year, 12, 0);

            return {
                start,
                end,
                effectiveEnd: getEffectiveEndDate(end),
                mode: 'year',
                label: `${analyticsPeriod.year}`,
                granularity: 'month',
            };
        }

        const firstPurchaseDate = getFirstPurchaseDate();
        const start = new Date(firstPurchaseDate.getFullYear(), firstPurchaseDate.getMonth(), 1);

        return {
            start,
            end: today,
            effectiveEnd: today,
            mode: 'all_time',
            label: 'ALL TIME',
            granularity: 'month',
        };
    }, [analyticsPeriod, selectedMonth, selectedYear, getMonthRange, getEffectiveEndDate, getFirstPurchaseDate]);

    const calculateInventoryValueForDate = useCallback((date) => {
        return getCombinedItems().reduce((total, manga) => {
            const purchaseDate = new Date(manga.purchasedDate);
            const saleDate = manga.saleDate ? new Date(manga.saleDate) : null;

            if (purchaseDate <= date && (!saleDate || saleDate >= date)) {
                return total + getInventoryValue(manga);
            }

            return total;
        }, 0);
    }, [getCombinedItems, getInventoryValue]);

    const getCurrentQuarter = () => {
        return Math.floor(new Date().getMonth() / 3) + 1;
    };

    const isAnalyticsPeriodNextDisabled = () => {
        if (!analyticsPeriod || analyticsPeriod.mode === 'all_time') {
            return true;
        }

        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() + 1;

        if (analyticsPeriod.mode === 'month') {
            return analyticsPeriod.year > currentYear ||
                (analyticsPeriod.year === currentYear && analyticsPeriod.month >= currentMonth);
        }

        if (analyticsPeriod.mode === 'quarter') {
            return analyticsPeriod.year > currentYear ||
                (analyticsPeriod.year === currentYear && analyticsPeriod.quarter >= getCurrentQuarter());
        }

        return analyticsPeriod.year >= currentYear;
    };

    const isAnalyticsPeriodPreviousDisabled = () => {
        if (!analyticsPeriod || analyticsPeriod.mode === 'all_time' || getCombinedItems().length === 0) {
            return true;
        }

        const firstPurchaseDate = getFirstPurchaseDate();
        const firstYear = firstPurchaseDate.getFullYear();
        const firstMonth = firstPurchaseDate.getMonth() + 1;
        const firstQuarter = Math.floor(firstPurchaseDate.getMonth() / 3) + 1;

        if (analyticsPeriod.mode === 'month') {
            return analyticsPeriod.year < firstYear ||
                (analyticsPeriod.year === firstYear && analyticsPeriod.month <= firstMonth);
        }

        if (analyticsPeriod.mode === 'quarter') {
            return analyticsPeriod.year < firstYear ||
                (analyticsPeriod.year === firstYear && analyticsPeriod.quarter <= firstQuarter);
        }

        return analyticsPeriod.year <= firstYear;
    };

    const handleChangeAnalyticsPeriod = (delta) => {
        if (!analyticsPeriod || !onAnalyticsPeriodChange || analyticsPeriod.mode === 'all_time') {
            return;
        }

        if (analyticsPeriod.mode === 'month') {
            const date = new Date(analyticsPeriod.year, analyticsPeriod.month - 1 + delta, 1);
            onAnalyticsPeriodChange({
                year: date.getFullYear(),
                month: date.getMonth() + 1,
                quarter: Math.floor(date.getMonth() / 3) + 1,
            });
            return;
        }

        if (analyticsPeriod.mode === 'quarter') {
            const zeroBasedQuarter = analyticsPeriod.quarter - 1 + delta;
            const yearOffset = Math.floor(zeroBasedQuarter / 4);
            const normalizedQuarter = ((zeroBasedQuarter % 4) + 4) % 4;

            onAnalyticsPeriodChange({
                year: analyticsPeriod.year + yearOffset,
                quarter: normalizedQuarter + 1,
                month: normalizedQuarter * 3 + 1,
            });
            return;
        }

        onAnalyticsPeriodChange({
            year: analyticsPeriod.year + delta,
        });
    };

    const getAnalyticsPeriodStepLabel = (delta) => {
        if (!analyticsPeriod) {
            return delta < 0 ? 'Previous Month' : 'Next Month';
        }

        if (analyticsPeriod.mode === 'quarter') {
            return delta < 0 ? 'Previous Quarter' : 'Next Quarter';
        }

        if (analyticsPeriod.mode === 'year') {
            return delta < 0 ? 'Previous Year' : 'Next Year';
        }

        return delta < 0 ? 'Previous Month' : 'Next Month';
    };

    const hasInventoryBalanceInMonth = useCallback((month, year) => {
        const { start, end } = getMonthRange(month, year);

        return getCombinedItems().some(manga => {
            const purchaseDate = new Date(manga.purchasedDate);
            const saleDate = manga.saleDate ? new Date(manga.saleDate) : null;

            return purchaseDate <= end && (!saleDate || saleDate >= start);
        });
    }, [getCombinedItems, getMonthRange]);

    const updateInventoryValueGraph = useCallback((month, year) => {
        const periodRange = analyticsPeriod
            ? getAnalyticsPeriodRange()
            : {
                ...getMonthRange(month, year),
                effectiveEnd: getEffectiveEndDate(getMonthRange(month, year).end),
                mode: 'month',
                label: `${new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long' }).toUpperCase()} ${year}`,
                granularity: 'day',
            };

        const amountPerPoint = [];
        let iterationDate = new Date(periodRange.start);

        while (iterationDate <= periodRange.end) {
            const pointEnd = periodRange.granularity === 'month'
                ? new Date(iterationDate.getFullYear(), iterationDate.getMonth() + 1, 0)
                : new Date(iterationDate);
            const cappedPointEnd = getEffectiveEndDate(pointEnd);

            amountPerPoint.push({
                date: new Date(periodRange.granularity === 'month' ? cappedPointEnd : iterationDate),
                amount: iterationDate <= periodRange.effectiveEnd
                    ? calculateInventoryValueForDate(cappedPointEnd)
                    : null,
            });

            if (periodRange.granularity === 'month') {
                iterationDate = new Date(iterationDate.getFullYear(), iterationDate.getMonth() + 1, 1);
            } else {
                iterationDate.setDate(iterationDate.getDate() + 1);
            }
        }

        setInventoryValueGraph(amountPerPoint);
    }, [analyticsPeriod, getAnalyticsPeriodRange, getMonthRange, getEffectiveEndDate, calculateInventoryValueForDate]);

    const getLastGraphAmount = () => {
        const lastValue = [...inventoryValueGraph]
            .reverse()
            .find((entry) => entry.amount !== null);

        return lastValue ? lastValue.amount : 0;
    };

    const currentDate = new Date();

    const isMonthDisabled = (month, year) => {
        const selectedDate = new Date(year, month - 1);
        return selectedDate > currentDate;
    };

    const isPastMonthDisabled = (month, year) => {
        const previousMonthDate = new Date(year, month - 2);
        const previousMonthYear = previousMonthDate.getFullYear();
        const previousMonth = previousMonthDate.getMonth() + 1;

        return !hasInventoryBalanceInMonth(previousMonth, previousMonthYear);
    };

    const isMonthWithNoInventoryBalance = (month, year) => {
        return !hasInventoryBalanceInMonth(month, year);
    };

    const availableYears = useMemo(() => {
        const combinedItems = getCombinedItems();
        if (combinedItems.length === 0) return [];

        const purchaseDates = combinedItems.map((item) => new Date(item.purchasedDate));
        const firstYear = new Date(Math.min(...purchaseDates)).getFullYear();
        const currentYear = new Date().getFullYear();

        return Array.from(
            { length: currentYear - firstYear + 1 },
            (_, index) => firstYear + index
        );
    }, [getCombinedItems]);

    const handleYearChange = (newYear) => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        const validMonths = [...Array(12).keys()]
            .map((month) => month + 1)
            .filter((month) => !isMonthDisabled(month, newYear) && hasInventoryBalanceInMonth(month, newYear));

        if (validMonths.length === 0) {
            return;
        }

        const selectedMonth = newYear === currentYear && validMonths.includes(currentMonth)
            ? currentMonth
            : validMonths[validMonths.length - 1];

        setSelectedYear(newYear);
        setSelectedMonth(selectedMonth);
    };

    const handleChangeMonth = (delta) => {
        let newMonth = selectedMonth + delta;
        let newYear = selectedYear;

        if (newMonth > 12) {
            newMonth = 1;
            newYear += 1;
        } else if (newMonth < 1) {
            newMonth = 12;
            newYear -= 1;
        }

        if (isMonthWithNoInventoryBalance(newMonth, newYear)) {
            return;
        }

        setSelectedMonth(newMonth);
        setSelectedYear(newYear);
    };

    const formatChangePercentage = (inventoryValueToday, startingInventoryValue) => {
        if (!Number.isFinite(inventoryValueToday) || !Number.isFinite(startingInventoryValue)) {
            return '/';
        }

        if (startingInventoryValue === 0) {
            return inventoryValueToday === 0 ? '0%' : '/';
        }

        let percentage = ((inventoryValueToday - startingInventoryValue) / startingInventoryValue * 100).toFixed(0);

        if (percentage < -999) percentage = "-999+";
        if (percentage > 999) percentage = "999+";

        return `${percentage}%`;
    };

    const periodRange = getAnalyticsPeriodRange();
    const previousPeriodEnd = new Date(periodRange.start);
    previousPeriodEnd.setDate(previousPeriodEnd.getDate() - 1);
    const startingInventoryValueForPeriod = calculateInventoryValueForDate(previousPeriodEnd);
    const endingInventoryValueForPeriod = getLastGraphAmount();
    const periodInventoryValue = endingInventoryValueForPeriod - startingInventoryValueForPeriod;

    const chipAmount30Days = (() => {
        if (startingInventoryValueForPeriod === 0) {
            return endingInventoryValueForPeriod - startingInventoryValueForPeriod;
        }

        return (endingInventoryValueForPeriod - startingInventoryValueForPeriod) / startingInventoryValueForPeriod * 100;
    })();

    const changePercentageDisplay = formatChangePercentage(
        endingInventoryValueForPeriod,
        startingInventoryValueForPeriod
    );

    const chipColor30Days = (() => {
        if (changePercentageDisplay === '/') return "gray";
        return chipAmount30Days >= 0 ? "green" : "red";
    })();

    const TooltipContent = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0];
            if (data.payload.amount !== null) {
                const date = new Date(data.payload.date);
                const day = date.getDate().toString().padStart(2, '0');
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const formattedDate = periodRange.granularity === 'month'
                    ? `${month}.${date.getFullYear()}.`
                    : `${day}.${month}.`;

                return (
                    <div className="p-1 pl-2 pr-2">
                        <p className="text-gray-900 border-b-2">{formattedDate}</p>
                        <p className="font-semibold text-right text-green-chart mt-1 mb-1">
                            {data.payload.amount.toLocaleString('sr-RS', { style: 'currency', currency: 'RSD', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>
                );
            }
        }

        return null;
    };

    const handleOpen = () => {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear();

        setSelectedMonth(currentMonth);
        setSelectedYear(currentYear);
        updateInventoryValueGraph(currentMonth, currentYear);
        setOpen(!open);
    };

    useEffect(() => {
        updateInventoryValueGraph(selectedMonth, selectedYear);
    }, [selectedMonth, selectedYear, availableItems, soldItems, updateInventoryValueGraph]);

    return (
        <>
            <li className="py-1.5">
                <Link onClick={handleOpen}>
                    <ListItem className="flex items-center space-x-4 text-left p-0 focus:bg-green-50 dark:focus:bg-cyan-900 hover:bg-green-50 dark:hover:bg-cyan-900">
                        <div className="flex-1 min-w-0">
                            <p className="text-md font-medium text-gray-900 truncate dark:text-gray-300">
                                Inventory Value
                            </p>

                            <div className="flex items-center space-x-2 mt-3">
                                <p className="text-sm text-green-500">
                                    {changePercentageDisplay}
                                </p>
                                <ArrowLongUpIcon strokeWidth={2.5} className="w-4 h-4 text-green-500" />
                            </div>
                        </div>

                        <div className="text-right">
                            <div className="inline-flex items-center text-base font-semibold text-gray-900 dark:text-gray-300">
                                {valueFormatter(analyticsPeriod && analyticsPeriod.mode !== 'all_time' ? periodInventoryValue : (analyticsPeriod ? endingInventoryValueForPeriod : dataValue))}
                            </div>
                        </div>

                        <div className="ml-2">
                            <ArrowLongRightIcon className="h-5 w-5 text-cyan-900 dark:text-cyan-200" />
                        </div>
                    </ListItem>
                </Link>
            </li>

            <Dialog
                open={open}
                handler={handleOpen}
                animate={{
                    mount: { scale: 1, y: 0 },
                    unmount: { scale: 0.9, y: -100 },
                }}
                className="p-1 max-h-[calc(100vh-4rem)] overflow-y-auto"
            >
                <DialogHeader>Inventory Value</DialogHeader>
                <DialogBody>
                    <div>
                        {analyticsPeriod && analyticsPeriod.mode !== 'all_time' && (
                            <div className="flex items-center justify-between mb-4">
                                <Button
                                    size="sm"
                                    variant="text"
                                    className="text-gray-800"
                                    onClick={() => handleChangeAnalyticsPeriod(-1)}
                                    disabled={isAnalyticsPeriodPreviousDisabled()}
                                >
                                    {getAnalyticsPeriodStepLabel(-1)}
                                </Button>

                                <Button
                                    size="sm"
                                    variant="text"
                                    className="text-gray-800"
                                    onClick={() => handleChangeAnalyticsPeriod(1)}
                                    disabled={isAnalyticsPeriodNextDisabled()}
                                >
                                    {getAnalyticsPeriodStepLabel(1)}
                                </Button>
                            </div>
                        )}

                        {!analyticsPeriod && (
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <Button
                                        size="sm"
                                        variant="text"
                                        className="text-gray-800"
                                        onClick={() => handleChangeMonth(-1)}
                                        disabled={isPastMonthDisabled(selectedMonth, selectedYear)}
                                    >
                                        Previous Month
                                    </Button>

                                    <Button
                                        size="sm"
                                        variant="text"
                                        className="text-gray-800"
                                        onClick={() => handleChangeMonth(1)}
                                        disabled={isMonthDisabled(selectedMonth + 1, selectedYear)}
                                    >
                                        Next Month
                                    </Button>
                                </div>

                                <div className="flex items-center justify-between">
                                    <label className="text-sm">Select Month</label>
                                    <select
                                        value={selectedMonth}
                                        onChange={(e) => setSelectedMonth(Number(e.target.value))}
                                        className="border-2 border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {[...Array(12).keys()].map((month) => (
                                            <option
                                                key={month + 1}
                                                value={month + 1}
                                                disabled={
                                                    isMonthDisabled(month + 1, selectedYear) ||
                                                    isMonthWithNoInventoryBalance(month + 1, selectedYear)
                                                }
                                                className={
                                                    isMonthDisabled(month + 1, selectedYear) ||
                                                    isMonthWithNoInventoryBalance(month + 1, selectedYear)
                                                        ? 'text-gray-400'
                                                        : 'text-black'
                                                }
                                            >
                                                {new Date(0, month).toLocaleString('default', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center justify-between mt-4">
                                    <label className="text-sm">Select Year</label>
                                    <select
                                        value={selectedYear}
                                        onChange={(e) => handleYearChange(Number(e.target.value))}
                                        className="border-2 border-gray-300 rounded-lg px-4 py-2 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        {availableYears.map((year) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <p className="text-xs font-medium text-gray-900 truncate">
                                    {periodRange.label}
                                </p>
                                <p className="text-xs font-medium text-gray-900 truncate">
                                    VS PREVIOUS PERIOD
                                </p>
                            </div>
                            <div className="flex items-center justify-between">
                                <Typography variant="h2" className="text-gray-900 mb-4">
                                    {endingInventoryValueForPeriod?.toLocaleString('sr-RS', {
                                        style: 'currency',
                                        currency: 'RSD',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </Typography>
                                <Chip size="md" value={changePercentageDisplay} className="mb-4 text-sm" color={chipColor30Days} />
                            </div>
                        </div>

                        <div>
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart className="right-4" data={inventoryValueGraph}
                                           margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="chartGreen" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
                                            <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <XAxis dataKey="date"
                                           tick={{ fontSize: 12, dy: 6 }} tickFormatter={(tick) => {
                                        const date = new Date(tick);
                                        const day = date.getDate().toString().padStart(2, '0');
                                        const month = (date.getMonth() + 1).toString().padStart(2, '0');
                                        return periodRange.granularity === 'month'
                                            ? `${month}.${date.getFullYear().toString().slice(-2)}.`
                                            : `${day}.${month}.`;
                                    }} />
                                    <YAxis tick={{ fontSize: 15, dx: -3 }} />
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <Tooltip cursor={{ fill: '#E8F5E9' }} payloadArray={inventoryValueGraph}
                                             content={<TooltipContent />}
                                             wrapperStyle={{ background: 'white', border: '2px solid #ddd', borderRadius: '8px', padding: '5px' }} offset={25} />
                                    <Area
                                        type="monotone"
                                        dataKey="amount"
                                        stroke="#82ca9d"
                                        fillOpacity={1}
                                        fill="url(#chartGreen)"
                                        isAnimationActive={false}
                                    />
                                    <Area
                                        type="monotone"
                                        dataKey="amount"
                                        stroke="transparent"
                                        fillOpacity={0.2}
                                        fill="url(#chartGray)"
                                        isAnimationActive={false}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="mt-4 text-sm text-gray-700">
                            <div className="flex justify-between mt-2">
                                <p>Ending balance of the {analyticsPeriod ? 'period' : 'month'}:</p>
                                <p>
                                    {inventoryValueGraph.length > 0 && endingInventoryValueForPeriod !== null
                                        ? endingInventoryValueForPeriod.toLocaleString('sr-RS', {
                                            style: 'currency',
                                            currency: 'RSD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })
                                        : '/'}
                                </p>
                            </div>
                            <div className="flex justify-between">
                                <p>Starting balance of the {analyticsPeriod ? 'period' : 'month'}:</p>
                                <p>
                                    {inventoryValueGraph.length > 0 && startingInventoryValueForPeriod !== null
                                        ? startingInventoryValueForPeriod.toLocaleString('sr-RS', {
                                            style: 'currency',
                                            currency: 'RSD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })
                                        : '/'}
                                </p>
                            </div>
                            <div className="flex justify-between mt-2 font-semibold">
                                <p>Inventory value for the selected {analyticsPeriod ? 'period' : 'month'}:</p>
                                <p>
                                    {inventoryValueGraph.length > 0 && periodInventoryValue !== null
                                        ? periodInventoryValue.toLocaleString('sr-RS', {
                                            style: 'currency',
                                            currency: 'RSD',
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })
                                        : '/'}
                                </p>
                            </div>
                        </div>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" color="black" onClick={handleOpen} className="mv-secondary-button">
                        <XMarkIcon className="h-4 w-4" />
                        Cancel
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    );
}
